<script setup lang="ts">
import { ChevronDown, ChevronUp, Send } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

interface Props {
  customerFirstName: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const emit = defineEmits<{
  send: [body: string]
}>()

const body = ref('')
const showTemplates = ref(false)

// SMS character limit helpers
const SMS_LIMIT = 160
const charCount = computed(() => body.value.length)
const segmentCount = computed(() => Math.max(1, Math.ceil(charCount.value / SMS_LIMIT)))
const overLimit = computed(() => charCount.value > SMS_LIMIT)

const canSend = computed(() => body.value.trim().length > 0 && !props.disabled)

function handleSend() {
  if (!canSend.value) return
  emit('send', body.value.trim())
  body.value = ''
}

function handleKeydown(event: KeyboardEvent) {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault()
    handleSend()
  }
}

// Quick-fill templates (pre-fill composer, don't auto-send)
const templates = [
  {
    label: 'Checking in',
    text: `Hi ${props.customerFirstName}, just checking in on your application. Feel free to reach out if you have any questions.`,
  },
  {
    label: 'Docs needed',
    text: `Hi ${props.customerFirstName}, we need a few documents to continue processing your application. Please upload them at your earliest convenience.`,
  },
  {
    label: 'Bank statement',
    text: `Hi ${props.customerFirstName}, we require your bank statements to proceed. Please upload the last 3 months of statements.`,
  },
  {
    label: 'Additional docs',
    text: `Hi ${props.customerFirstName}, we need some additional documents from you. Our team will be in touch with the specifics.`,
  },
]

function applyTemplate(text: string) {
  body.value = text
  showTemplates.value = false
}
</script>

<template>
  <div class="bg-background px-4 py-3">
    <!-- Quick templates toggle -->
    <div class="mb-2">
      <button
        class="flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        type="button"
        @click="showTemplates = !showTemplates"
      >
        <span>Quick templates</span>
        <ChevronUp v-if="showTemplates" class="size-3" />
        <ChevronDown v-else class="size-3" />
      </button>

      <!-- Template chips -->
      <div v-if="showTemplates" class="mt-2 flex flex-wrap gap-1.5">
        <button
          v-for="tpl in templates"
          :key="tpl.label"
          class="rounded-full border border-border bg-muted/60 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          type="button"
          @click="applyTemplate(tpl.text)"
        >
          {{ tpl.label }}
        </button>
      </div>
    </div>

    <!-- Composer row -->
    <div class="flex items-end gap-2">
      <div class="relative min-w-0 flex-1">
        <Textarea
          v-model="body"
          placeholder="Type a message…"
          :disabled="disabled"
          class="max-h-32 min-h-lh resize-none field-sizing-content pr-14 leading-relaxed"
          :rows="1"
          @keydown="handleKeydown"
        />

        <!-- Character counter -->
        <span
          :class="[
            'pointer-events-none absolute right-2 bottom-2 text-[10px] tabular-nums',
            overLimit ? 'text-destructive font-semibold' : 'text-muted-foreground',
          ]"
        >
          {{ charCount }}<template v-if="overLimit"> ({{ segmentCount }} SMS)</template>
        </span>
      </div>

      <Button
        :disabled="!canSend"
        size="icon"
        class="mb-0 shrink-0"
        title="Send (Ctrl+Enter)"
        @click="handleSend"
      >
        <Send class="size-4" />
        <span class="sr-only">Send</span>
      </Button>
    </div>

    <p class="mt-1 text-[10px] text-muted-foreground">Ctrl+Enter to send</p>
  </div>
</template>
